import React, { useState, useRef, useEffect } from 'react';
import { VoteType, VOTE_TYPES } from '@/services/reactionsService';

interface VotingInterfaceProps {
  onVoteSubmit: (voteType: VoteType, strength: number, comment?: string, isAnonymous?: boolean) => Promise<void>;
  currentVote?: any | null;
  disabled?: boolean;
  showStrength?: boolean;
  allowComments?: boolean;
  allowAnonymous?: boolean;
}

export function VotingInterface({ 
  onVoteSubmit,
  currentVote,
  disabled = false,
  showStrength = true,
  allowComments = true,
  allowAnonymous = true
}: VotingInterfaceProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVoteType, setSelectedVoteType] = useState<VoteType | null>(currentVote?.voteType || null);
  const [voteStrength, setVoteStrength] = useState(currentVote?.voteStrength || 1);
  const [comment, setComment] = useState(currentVote?.comment || '');
  const [isAnonymous, setIsAnonymous] = useState(currentVote?.isAnonymous || false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleVoteSubmit = async () => {
    if (!selectedVoteType || disabled || isLoading) return;

    setIsLoading(true);
    try {
      await onVoteSubmit(
        selectedVoteType, 
        showStrength ? voteStrength : 1, 
        allowComments && comment.trim() ? comment.trim() : undefined,
        allowAnonymous ? isAnonymous : false
      );
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to submit vote:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickVote = async (voteType: VoteType) => {
    if (disabled || isLoading) return;

    setIsLoading(true);
    try {
      await onVoteSubmit(voteType, 1, undefined, false);
    } catch (error) {
      console.error('Failed to submit quick vote:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentVoteData = () => {
    return currentVote && currentVote.voteType ? VOTE_TYPES[currentVote.voteType as VoteType] : null;
  };

  const currentVoteData = getCurrentVoteData();

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Current Vote Display */}
      {currentVoteData ? (
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled || isLoading}
          className={`
            inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all duration-200
            bg-white border-gray-300 text-gray-900 shadow-sm hover:shadow-md
            ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          `}
          style={{ borderColor: currentVoteData.color }}
        >
          <span className="text-lg" role="img" aria-label={currentVoteData.label}>
            {currentVoteData.icon}
          </span>
          <span className="text-sm font-medium">{currentVoteData.label}</span>
          {currentVote.voteStrength > 1 && (
            <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded-full">
              {currentVote.voteStrength}x
            </span>
          )}
          <svg className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
               fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      ) : (
        /* Quick Vote Buttons */
        <div className="flex items-center gap-1">
          {(['upvote', 'downvote', 'must_have'] as VoteType[]).map((voteType) => {
            const voteData = VOTE_TYPES[voteType];
            return (
              <button
                key={voteType}
                onClick={() => handleQuickVote(voteType)}
                disabled={disabled || isLoading}
                className={`
                  p-2 rounded-lg transition-all duration-200 hover:bg-gray-50
                  ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                `}
                style={{ color: voteData.color }}
                title={voteData.description}
                aria-label={voteData.label}
              >
                <span className="text-lg">{voteData.icon}</span>
              </button>
            );
          })}
          <button
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled || isLoading}
            className={`
              p-2 rounded-lg transition-all duration-200 hover:bg-gray-50 text-gray-600
              ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
            `}
            title="More voting options"
            aria-label="More voting options"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>
      )}

      {/* Advanced Voting Panel */}
      {isOpen && (
        <div className="absolute z-50 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 py-4">
          <div className="px-4 py-2 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Cast Your Vote</h3>
            <p className="text-xs text-gray-500 mt-1">Help the group make a decision</p>
          </div>
          
          {/* Vote Type Selection */}
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vote Type
              </label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(VOTE_TYPES).map(([key, vote]) => {
                  const voteType = key as VoteType;
                  const isSelected = selectedVoteType === voteType;
                  
                  return (
                    <button
                      key={voteType}
                      onClick={() => setSelectedVoteType(voteType)}
                      className={`
                        flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 text-left
                        ${isSelected 
                          ? 'bg-blue-50 border border-blue-200 text-blue-900' 
                          : 'hover:bg-gray-50 border border-gray-200'
                        }
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                      `}
                      style={{ 
                        borderColor: isSelected ? vote.color : undefined,
                        backgroundColor: isSelected ? `${vote.color}10` : undefined 
                      }}
                    >
                      <span className="text-lg" role="img" aria-label={vote.label}>
                        {vote.icon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{vote.label}</div>
                        <div className="text-xs text-gray-500 truncate">{vote.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Vote Strength */}
            {showStrength && selectedVoteType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vote Strength: {voteStrength}x
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={voteStrength}
                  onChange={(e) => setVoteStrength(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, ${VOTE_TYPES[selectedVoteType]?.color || '#3b82f6'} 0%, ${VOTE_TYPES[selectedVoteType]?.color || '#3b82f6'} ${(voteStrength - 1) * 25}%, #e5e7eb ${(voteStrength - 1) * 25}%, #e5e7eb 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Weak</span>
                  <span>Strong</span>
                </div>
              </div>
            )}

            {/* Comment */}
            {allowComments && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment (optional)
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Share your thoughts..."
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {comment.length}/500 characters
                </div>
              </div>
            )}

            {/* Anonymous Option */}
            {allowAnonymous && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="anonymous-vote"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="anonymous-vote" className="text-sm text-gray-700">
                  Vote anonymously
                </label>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between px-4 pt-3 border-t border-gray-100">
            {currentVote && (
              <button
                onClick={() => handleVoteSubmit()}
                disabled={!selectedVoteType || isLoading}
                className="text-sm text-red-600 hover:text-red-700 font-medium focus:outline-none focus:underline"
              >
                Remove Vote
              </button>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => setIsOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-700 focus:outline-none focus:underline"
              >
                Cancel
              </button>
              <button
                onClick={handleVoteSubmit}
                disabled={!selectedVoteType || isLoading}
                className={`
                  px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200
                  ${selectedVoteType && !isLoading
                    ? 'bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }
                `}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </div>
                ) : (
                  'Submit Vote'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}